import { Alert, Button, Card, Col, Divider, message, Row, Skeleton, Space, Steps, Typography } from "antd";
import { useSurveyContext } from "context/SurveyContext";
import { useSurveyPage } from "../../context/SurveyPageContext";
import PrintCoverageConfirmationButton from "../../pages/negotiations/PrintCoverageConfirmationButton";
import React, { useMemo } from "react";
import ConfirmSubmissionButton from "./ConfirmSubmissionButton";
import DeclineSubmissionButton from "./DeclineSubmissionButton";
import ModifySubmissionButton from "./ModifySubmissionButton";
import { CoverageConfirmation, NegotiationModel } from "../../types/negotiations";
import CoverageHistoryModalButton from "./CoverageHistoryModalButton";
import { useLazyGetDocumentUrlQuery } from "../../redux/api/documentsApiSlice";
import useFileDownloader from "../../hooks/useFileDownloader";
import { FileOutlined } from "@ant-design/icons";
import { States } from "../../types";
import {useLazyDownloadChatHistoryQuery} from "../../redux/api/negotiationsApiSlice";
import {GenerateRiskStatus} from "./GenerateRiskStatus";
import {IncreasePremium} from "./IncreasePremium";
import {ImportPolicyData} from "./ImportPolicyData";

const { Title } = Typography;
const { Step } = Steps;

export enum SubmissionStates {
  RECEIVED = "0010",
  OTHER_INFO = "0030",
  QUOTATION_SENT = "0050",
  DECLINED = "0100",
  CONFIRMATION_SENT = "0200"
}

export function getSubmissionStateLabel(state: SubmissionStates | null) {
  let label: string = "";
  switch (state) {
    case SubmissionStates.QUOTATION_SENT:
      label = "Quotazione inviata";
      break;
    case SubmissionStates.DECLINED:
      label = "Declinata";
      break;
    case SubmissionStates.CONFIRMATION_SENT:
      label = "Conferma inviata";
      break;
    case SubmissionStates.RECEIVED:
      label = "Ricevuta";
      break;
    case SubmissionStates.OTHER_INFO:
      label = "Approfondimenti";
      break;
    default:
  }
  return label;
}

const SelectSurveyPageCard = ({ negotiation, coverageData, isSurveyDisabled }: {
  negotiation: NegotiationModel,
  coverageData: CoverageConfirmation,
  isSurveyDisabled: boolean
}) => {


  const [survey] = useSurveyContext();
  const { page, setPage, staticSurveyPagesNumber, isAdditionalInsuredVisible } = useSurveyPage();
  const [getUrl] = useLazyGetDocumentUrlQuery();
  const [getChatHistory, {isLoading}] = useLazyDownloadChatHistoryQuery()
  const { downloadFile } = useFileDownloader();

  const handleChange = (page: number) => {
    setPage(page);
  };

  const handleOpenDocument = (docID: string | undefined) => {
    if (docID)
      getUrl({ negotiation_uuid: negotiation.uuid, uuid: docID }).unwrap()
        .then((response) => {
            downloadFile(response.url);
          }
        )
        .catch((e: any) => {
          message.error("Impossibile aprire il file");
          console.error("Open file", e);
        });
  };

  const submissionRequiresOtherInfo = useMemo(()=>coverageData.submission_state === SubmissionStates.OTHER_INFO, [coverageData.submission_state]);

  return (
    <>
      <Card>
        <Space direction={"vertical"} size={8} style={{ width: "100%" }}>
          <Title level={5}>
            Indice pagine
          </Title>
          <Skeleton loading={!survey}>
            <Steps
              direction={"vertical"}
              size={"small"}
              onChange={handleChange}
              current={page}>
              <Step title={"1 - Dati generali"} key={0} />
              {isAdditionalInsuredVisible && <Step title={"2 - Assicurati addizionali"} key={1} />}
              {!!(staticSurveyPagesNumber && survey) && survey.visiblePages.map((page: any, index: number) => (
                <Step title={`${index + staticSurveyPagesNumber + 1} - ${page.title}`}
                      key={index + staticSurveyPagesNumber} />
              ))}
            </Steps>
          </Skeleton>
        </Space>
        <Divider style={{ margin: "0 0 1rem 0" }} />
        <Row justify={"end"}>
          <Col>
            <PrintCoverageConfirmationButton />
          </Col>
        </Row>
        {(negotiation.lob.can_manage_sov || negotiation.lob.can_extract_policy_data) && <Divider/>}
        {negotiation.lob.can_manage_sov && <Row style={{marginTop: 10}} justify={"space-between"}>
          <Col>
            {negotiation.coverageconfirmation && <IncreasePremium isSurveyDisabled={isSurveyDisabled} cUuid={negotiation.coverageconfirmation}/>}
          </Col>
          <Col>
            <GenerateRiskStatus coverageData={coverageData}/>
          </Col>
        </Row>}
        {negotiation.lob.can_extract_policy_data && negotiation.coverageconfirmation && <Row style={{marginTop: 10}}>
          <ImportPolicyData cUuid={negotiation.coverageconfirmation} isSurveyDisabled={isSurveyDisabled}/>
        </Row>}
        {coverageData.can_handle_submission_state && <>
          <Divider style={{margin: "1rem 0 1rem 0"}}/>
          <Row justify={"space-between"}>
              <div>
                <Title level={5}>
                  Gestione Submission
                </Title>
                Stato: <strong>{coverageData.submission_state_label}</strong>
              </div>
              <CoverageHistoryModalButton coverageID={coverageData.uuid}/>
          </Row>
          {coverageData.documents.length > 0 &&
            <Row justify={"space-between"} gutter={[8, 8]} style={{ marginTop: "1rem" }}>
              <Col xs={24}>
                {coverageData.documents.find(el => el.code === "SBM") &&
                  <Button type={"link"} icon={<FileOutlined />}
                          onClick={() => handleOpenDocument(coverageData.documents.find(el => el.code === "SBM")?.uuid)}>Submission</Button>}
              </Col>
              <Col xs={24}>
                {coverageData.documents.find(el => el.code === "QTLTT") &&
                  <Button type={"link"} icon={<FileOutlined />}
                          onClick={() => handleOpenDocument(coverageData.documents.find(el => el.code === "QTLTT")?.uuid)}>Lettera
                    di quotazione</Button>}
              </Col>
              <Col xs={24}>
                {coverageData.documents.find(el => el.code === "FRMRDR") &&
                  <Button type={"link"} icon={<FileOutlined />}
                          onClick={() => handleOpenDocument(coverageData.documents.find(el => el.code === "FRMRDR")?.uuid)}>Ordine
                    fermo</Button>}
              </Col>
              <Col xs={24}>
                {coverageData.documents.find(el => el.code === "CVRLTT") &&
                  <Button type={"link"} icon={<FileOutlined />}
                          onClick={() => handleOpenDocument(coverageData.documents.find(el => el.code === "CVRLTT")?.uuid)}>Conferma
                    di copertura</Button>}
              </Col>
            </Row>}
          {coverageData.submission_state !== SubmissionStates.CONFIRMATION_SENT && coverageData.submission_state !== SubmissionStates.DECLINED &&
            <Row justify={"space-between"} gutter={[8, 8]} style={{ marginTop: "1rem" }}>
              <Col xs={24}>
                <ConfirmSubmissionButton coverageConfirmation={coverageData} negotiation={negotiation}
                                         buttonProps={{ disabled: submissionRequiresOtherInfo }} />
              </Col>
              <Col xs={24} lg={12}>
                <ModifySubmissionButton coverageConfirmation={coverageData}
                                        buttonProps={{ disabled: submissionRequiresOtherInfo }} />
              </Col>
              <Col xs={24} lg={12}>
                <DeclineSubmissionButton coverageConfirmation={coverageData}
                                         buttonProps={{ disabled: submissionRequiresOtherInfo }} />
              </Col>
              <Col xs={24}>
                {coverageData.submission_state === SubmissionStates.QUOTATION_SENT && negotiation.state !== States.Bound && coverageData.documents.find(el => el.code === "FRMRDR") &&
                  <Alert
                    type={"warning"}
                    showIcon={true}
                    message="È necessario passare la trattativa in stato Bound prima di inviare la conferma di copertura."
                  />}
              </Col>
            </Row>}
          {negotiation?.chat_room_uuid !== null && <>
            <Divider/>
            <Row justify={"end"}>
              <Button
                  loading={isLoading}
                  onClick={() => getChatHistory(negotiation.uuid)}>
                Scarica chat
              </Button>
            </Row>
          </>}
        </>}
      </Card>
    </>
  );
};
export default SelectSurveyPageCard;

/*
* stati possibili
*
* -ricevuta
* - approfondimenti richiesti
* - approfondimenti
*
*
* */